import { useRef, useState, useEffect, useCallback } from 'react';
import ViinexService from '@/services/viinex';
import {
    WebRTCStatus,
    WebRTCState,
    WebRTCConnectionContext,
    WebRTCCleanupContext,
    WebRTCCallbacks,
} from '@/types/webrtc';
import Config from '@/config';
import { generateSessionId, normalizeSdp } from '@/utils/webrtc';

interface UseWebRTCOptions {
    sourceId: string;
    enabled: boolean;
}

const ICE_GATHERING_TIMEOUT = 5000;

export function useWebRTC({ sourceId, enabled }: UseWebRTCOptions) {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(
        null,
    );
    const webRtcStateRef = useRef<WebRTCState>({
        connection: null,
        sessionId: generateSessionId(),
        service: null,
        cleanupExecuted: false,
        streamSet: false,
    });

    const [status, setStatus] = useState<WebRTCStatus>(WebRTCStatus.IDLE);
    const [error, setError] = useState<string | null>(null);
    const [retryTrigger, setRetryTrigger] = useState(0);

    const videoCallbackRef = useCallback((node: HTMLVideoElement | null) => {
        videoRef.current = node;
        setVideoElement(node);
    }, []);

    useEffect(() => {
        if (!enabled || !videoElement) {
            setStatus(WebRTCStatus.IDLE);
            return;
        }

        webRtcStateRef.current.cleanupExecuted = false;
        webRtcStateRef.current.streamSet = false;

        setupWebRTCConnection({
            video: videoElement,
            sourceId,
            stateRef: webRtcStateRef,
            callbacks: {
                setStatus,
                setError,
            },
        });

        return () =>
            cleanupWebRTCConnection({
                video: videoElement,
                stateRef: webRtcStateRef,
            });
    }, [sourceId, enabled, retryTrigger, videoElement]);

    const retry = () => {
        webRtcStateRef.current.sessionId = generateSessionId();
        setRetryTrigger((prev) => prev + 1);
    };

    return { videoRef, videoCallbackRef, status, error, retry };
}

async function setupWebRTCConnection(context: WebRTCConnectionContext) {
    const { video, sourceId, stateRef, callbacks } = context;
    const { setStatus, setError } = callbacks;

    try {
        setStatus(WebRTCStatus.CONNECTING);
        setError(null);

        const webRtcService =
            await ViinexService.getWebRtcServiceName(sourceId);
        stateRef.current.service = webRtcService;

        const connection = createPeerConnection(video, stateRef, callbacks);
        stateRef.current.connection = connection;

        const offerSdp = await ViinexService.fetchWebRTCOffer(
            webRtcService,
            stateRef.current.sessionId,
            sourceId,
        );
        await connection.setRemoteDescription(
            createSessionDescription('offer', offerSdp),
        );

        const answer = await connection.createAnswer();

        const iceGatheringPromise = waitForIceGathering(connection);
        await connection.setLocalDescription(answer);

        await iceGatheringPromise;

        if (!connection.localDescription?.sdp) {
            throw new Error('Failed to generate local SDP');
        }

        await ViinexService.sendWebRTCAnswer(
            webRtcService,
            stateRef.current.sessionId,
            connection.localDescription.sdp,
        );
    } catch (err) {
        console.error('[setupWebRTCConnection] Error:', err);
        setError(err instanceof Error ? err.message : 'Connection failed');
        setStatus(WebRTCStatus.FAILED);
    }
}

function createPeerConnection(
    video: HTMLVideoElement,
    stateRef: { current: WebRTCState },
    callbacks: WebRTCCallbacks,
): RTCPeerConnection {
    const { setStatus, setError } = callbacks;

    const connection = new RTCPeerConnection({
        iceServers: [
            {
                urls: `stun:${new URL(Config.VITE_WEBRTC_STUN_SERVER_URL).hostname}:3478`,
            },
        ],
    });

    connection.ontrack = (event) => {
        if (event.streams[0] && !stateRef.current.streamSet) {
            stateRef.current.streamSet = true;
            video.srcObject = event.streams[0];
            video.play().catch((err) => {
                if (err.name !== 'AbortError') {
                    console.error('Play failed:', err);
                }
            });
            setStatus(WebRTCStatus.CONNECTED);
        }
    };

    connection.oniceconnectionstatechange = () => {
        const state = connection.iceConnectionState;
        if (state === 'failed') {
            setError('WebRTC connection failed');
            setStatus(WebRTCStatus.FAILED);
        } else if (state === 'disconnected') {
            setError('WebRTC connection disconnected');
            setStatus(WebRTCStatus.DISCONNECTED);
        }
    };

    return connection;
}

function createSessionDescription(
    type: 'offer' | 'answer',
    sdp: string,
): RTCSessionDescriptionInit {
    return {
        type,
        sdp: normalizeSdp(sdp),
    };
}

function waitForIceGathering(connection: RTCPeerConnection): Promise<void> {
    return new Promise((resolve, reject) => {
        if (connection.iceGatheringState === 'complete') {
            resolve();
            return;
        }

        let candidatesReceived = 0;
        const timeout = setTimeout(() => {
            if (candidatesReceived > 0) {
                cleanup();
                resolve();
            } else {
                cleanup();
                reject(new Error('ICE gathering timeout with no candidates'));
            }
        }, ICE_GATHERING_TIMEOUT);

        const handleGatheringStateChange = () => {
            if (connection.iceGatheringState === 'complete') {
                cleanup();
                resolve();
            }
        };

        const handleIceCandidate = (event: RTCPeerConnectionIceEvent) => {
            if (event.candidate) {
                candidatesReceived++;
            } else {
                cleanup();
                resolve();
            }
        };

        const cleanup = () => {
            clearTimeout(timeout);
            connection.removeEventListener(
                'icegatheringstatechange',
                handleGatheringStateChange,
            );
            connection.removeEventListener('icecandidate', handleIceCandidate);
        };

        connection.addEventListener(
            'icegatheringstatechange',
            handleGatheringStateChange,
        );
        connection.addEventListener('icecandidate', handleIceCandidate);
    });
}

function cleanupWebRTCConnection(context: WebRTCCleanupContext) {
    const { video, stateRef } = context;

    if (stateRef.current.cleanupExecuted) return;
    stateRef.current.cleanupExecuted = true;

    if (stateRef.current.connection) {
        stateRef.current.connection.close();
        stateRef.current.connection = null;
    }

    if (video) {
        video.pause();
        video.srcObject = null;
    }

    if (stateRef.current.service) {
        ViinexService.deleteWebRTCSession(
            stateRef.current.service,
            stateRef.current.sessionId,
        );
    }
}
